import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import TrialCountdown from "@/components/TrialCountdown";
import Footer from "@/components/Footer";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import { LayoutDashboard, Home, FileText, CheckSquare, Building2, LogOut, GraduationCap, Menu, HeartPulse, Gift, Briefcase, Users2, Crown, BookText, Settings as SettingsIcon, Sparkles, BarChart3, Accessibility, MessagesSquare, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import LanguageHelper from "@/components/LanguageHelper";
import NotificationCenter from "@/components/NotificationCenter";
import { useNoIndex } from "@/lib/useSeo";

const navItems = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/administrations", label: "Administrations", icon: Building2, testid: "nav-admins" },
  { to: "/logement", label: "Logement", icon: Home, testid: "nav-logement" },
  { to: "/jobs-argent", label: "Jobs & Argent", icon: Briefcase, testid: "nav-jobs" },
  { to: "/jobs-ai", label: "Outils IA Jobs", icon: Sparkles, testid: "nav-jobs-ai" },
  { to: "/bien-etre", label: "Bien-être", icon: HeartPulse, testid: "nav-bienetre" },
  { to: "/accessibilite", label: "Accessibilité & accompagnement", icon: Accessibility, testid: "nav-accessibilite" },
  { to: "/etudiants-internationaux", label: "Étudiants internationaux", icon: Globe2, testid: "nav-international" },
  { to: "/communaute", label: "Communauté", icon: Users2, testid: "nav-communaute" },
  { to: "/chat", label: "Chat communauté", icon: MessagesSquare, testid: "nav-chat" },
  { to: "/documents", label: "Mes documents", icon: FileText, testid: "nav-documents" },
  { to: "/demarches", label: "Mes démarches", icon: CheckSquare, testid: "nav-demarches" },
  { to: "/modeles", label: "Modèles & lettres", icon: BookText, testid: "nav-modeles" },
  { to: "/parrainage", label: "Parrainage", icon: Gift, testid: "nav-parrainage" },
  { to: "/premium", label: "Premium", icon: Crown, testid: "nav-premium" },
  { to: "/settings", label: "Paramètres", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadAcc, setUnreadAcc] = useState(0);
  const [communityOnline, setCommunityOnline] = useState(0);

  // All authenticated pages are private user spaces — never index in Google.
  useNoIndex();

  // Poll global online count for the "community alive" indicator in sidebar header.
  // Also sends a lightweight heartbeat so the user counts as "online" across the app.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      // Fire-and-forget heartbeat so the user counts as online (even when not on /chat)
      api.post("/accessibility/chat-room/presence").catch(() => {});
      try {
        const { data } = await api.get("/accessibility/chat-room/presence");
        if (!cancelled) setCommunityOnline(data.online_count || 0);
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 30000);  // every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  // Poll unread chat count for the community chat badge.
  // Pause polling while user is ON the chat page (the page handles read state).
  useEffect(() => {
    if (!user) return;
    const isOnChatPage = location.pathname === "/chat" || location.pathname.startsWith("/accessibilite");
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/accessibility/chat-room/unread");
        setUnreadAcc(data.unread_count || 0);
      } catch { /* silent */ }
    };
    if (isOnChatPage) {
      setUnreadAcc(0);
      return;
    }
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform fixed md:static z-40 inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex flex-col`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="relative w-9 h-9 rounded-xl bg-indigo-500 text-white flex items-center justify-center shrink-0">
              <GraduationCap size={20} />
              {communityOnline > 0 && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white"
                  aria-label={`${communityOnline} étudiant${communityOnline > 1 ? "s" : ""} en ligne`}
                  title={`${communityOnline} étudiant${communityOnline > 1 ? "·e·s" : "·e"} en ligne`}
                  data-testid="sidebar-online-dot"
                >
                  <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-lg leading-none text-slate-900 truncate">Student Hub</p>
              <p className="text-xs text-slate-500 mt-1 truncate">
                {communityOnline > 0
                  ? `${communityOnline} en ligne · communauté active`
                  : "Étudiant en France"}
              </p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <it.icon size={18} />
              <span className="flex-1">{it.label}</span>
              {it.to === "/chat" && unreadAcc > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold leading-none animate-pulse"
                  aria-label={`${unreadAcc} nouveau${unreadAcc > 1 ? "x" : ""} message${unreadAcc > 1 ? "s" : ""}`}
                  data-testid="nav-chat-unread-badge"
                >
                  {unreadAcc > 99 ? "99+" : unreadAcc}
                </span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `mt-4 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-t border-slate-100 pt-4 ${
                  isActive ? "bg-rose-50 text-rose-600" : "text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                }`
              }
            >
              <BarChart3 size={18} />
              <span>Admin · Stats</span>
            </NavLink>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-slate-100">
          <div className="text-xs text-slate-500 mb-2 truncate">{user?.email}</div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-slate-600 hover:text-red-600"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <EmailVerifyBanner />
        <TrialCountdown />
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2" data-testid="mobile-menu-btn">
            <Menu size={20} />
          </button>
          <span className="font-display font-semibold">Student Hub</span>
          <div className="md:hidden flex items-center gap-2">
            <NotificationCenter />
            <LanguageHelper />
          </div>
        </header>
        <div className="hidden md:flex items-center justify-end gap-3 px-6 sm:px-8 md:px-10 lg:px-12 pt-4">
          <NotificationCenter />
          <LanguageHelper />
        </div>
        <div className="p-6 sm:p-8 md:p-10 lg:p-12 max-w-7xl pt-4 md:pt-2">
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
}
